import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import {
    GlobalSearchResult,
    GlobalSearchResultPersonType,
    GlobalSearchResultType,
    GlobalSearchService
} from '@predictiveindex/pi-client-global-search-lib';
import { IconSize } from '@predictiveindex/tapestry';
import { debounce } from 'lodash-decorators';
import { peopleSearchDelay } from '../../../constants-types';
import { MyProfileTooltipService } from '../../../my-profile-tooltip/profile-tooltip/my-profile-tooltip.service';
import {
    FeatureFlags,
    FeatureFlagsService,
    Logger,
    UserProfileSettingsService
} from '../../../system-services';
import { GlobalSearchPersonSnapshotComponent } from '../global-search-person-snapshot';

@Component({
    selector: 'global-search-menu',
    templateUrl: './global-search-menu.component.html',
    styleUrls: ['./global-search-menu.component.less'],
    standalone: false
})
export class GlobalSearchMenuComponent {
    @ViewChild('searchBox')
    searchBox: ElementRef;
    @ViewChild('searchMenuTrigger')
    searchMenuTrigger: MatMenuTrigger;

    minChars = 3;
    maxDisplayResults = 5;
    truncatedTextLength = 40;
    iconSize = IconSize;
    searchValue = '';
    results: GlobalSearchResult[] = [];

    constructor(
        private readonly globalSearch: GlobalSearchService,
        private readonly userProfile: UserProfileSettingsService,
        private readonly router: Router,
        private readonly logger: Logger,
        private readonly featureFlagService: FeatureFlagsService,
        private readonly myProfileTooltipService: MyProfileTooltipService
    ) {}

    @debounce(peopleSearchDelay)
    async searchFunction(): Promise<void> {
        if (this.searchValue.length >= this.minChars) {
            this.results = await this.globalSearch.searchAll(
                this.userProfile.currentCompany,
                this.searchValue
            );
        } else {
            this.results = [];
        }
    }

    menuOpened(): void {
        this.searchBox.nativeElement.focus();
    }

    menuClosed(): void {
        this.searchValue = '';
        this.results = [];
    }

    async selectResult(result: GlobalSearchResult): Promise<void> {
        const isTransferredPerson =
            result.resultType !== GlobalSearchResultType.Job &&
            ((!result.email?.trim() &&
                result.personType !== GlobalSearchResultPersonType.Candidate) ||
                result.personType === GlobalSearchResultPersonType.Unknown ||
                result.personType === GlobalSearchResultPersonType.Other ||
                result.personType ===
                    GlobalSearchResultPersonType.FormerEmployee);
        if (
            this.featureFlagService.getByFlag(
                FeatureFlags.transferredPeopleGlobalSearchRedirect
            ) &&
            isTransferredPerson
        ) {
            void this.router.navigate(
                ['admin', 'users', 'transferred-people'],
                {
                    queryParams: {
                        filter:
                            result.email?.trim() ||
                            `${result.firstName} ${result.lastName}`.trim()
                    }
                }
            );
            return;
        }

        const hasTakenBa =
            !!this.myProfileTooltipService.person?.referencePattern;
        const canNavigate =
            (result.personType === GlobalSearchResultPersonType.Employee
                ? result.canViewInInspire
                : false) && hasTakenBa;

        this.globalSearch.openSearchResult(
            result,
            !!canNavigate,
            GlobalSearchPersonSnapshotComponent
        );

        this.logger.logInfo(`Global Search - result click`, {
            searchValue: this.searchValue,
            searchType: this.abstractResultType(result.resultType),
            destinationPage: this.abstractResultType(result.resultType),
            numberOfResults: this.results.length,
            pageOn: window.location.pathname
        });
    }

    abstractResultType(searchType: GlobalSearchResultType) {
        switch (searchType) {
            case GlobalSearchResultType.Person:
                return 'Person';
            case GlobalSearchResultType.PersonNoInspireAccess:
                return 'PersonPeekPanel';
            case GlobalSearchResultType.Candidate:
                return 'Candidate';
            case GlobalSearchResultType.Job:
                return 'Job';
            default:
                return 'Unknown';
        }
    }

    selectViewMore() {
        this.logger.logInfo(`Global Search - result page clicked`, {
            searchValue: this.searchValue,
            numberOfResults: this.results.length,
            pageOn: window.location.pathname
        });

        this.router.navigateByUrl(`/search?query=${this.searchValue}`);
        this.searchMenuTrigger.closeMenu();
    }
}
